import { FACTORY_ADDRESS as V3_FACTORY_ADDRESS } from "elephantswapv3-sdk";

import { SupportedChainId } from "./chains";

type AddressMap = { [chainId: number]: string };

const DEFAULT_NETWORKS = [
  SupportedChainId.HARMONY,
  SupportedChainId.MAINNET,
  SupportedChainId.GOERLI,
  SupportedChainId.SEPOLIA,
];

function constructSameAddressMap(
  address: string,
  additionalNetworks: SupportedChainId[] = []
): AddressMap {
  return DEFAULT_NETWORKS.concat(additionalNetworks).reduce<AddressMap>(
    (memo, chainId) => {
      memo[chainId] = address;
      return memo;
    },
    {}
  );
}

export const UNI_ADDRESS: AddressMap = constructSameAddressMap(
  "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984"
);

export const UNISWAP_NFT_AIRDROP_CLAIM_ADDRESS =
  "0x8B799381ac40b838BBA4131ffB26197C432AFe78";

export const V2_FACTORY_ADDRESSES: AddressMap = constructSameAddressMap(
  "0x0Dea90EC11032615E027664D2708BC292Bbd976B"
);
export const V2_ROUTER_ADDRESS: AddressMap = constructSameAddressMap(
  "0x9B5747c6C7e29Aab9545233D6310FC6db1e11F70"
);
export const HARMONY_UNIVERSAL_ROUTER_PERMIT2_ADDRESS =
  "0x1D796FAFFDA71a1db9eDbb3B0B0083F3252E84F1";
// celo v3 addresses
const CELO_V3_CORE_FACTORY_ADDRESSES =
  "0xAfE208a311B21f13EF87E33A90049fC17A7acDEc";
const CELO_V3_MIGRATOR_ADDRESSES = "0x3cFd4d48EDfDCC53D3f173F596f621064614C582";
const CELO_MULTICALL_ADDRESS = "0x633987602DE5C4F337e3DbF265303A1080324204";
const CELO_QUOTER_ADDRESSES = "0x82825d0554fA07f7FC52Ab63c961F330fdEFa8E8";
const CELO_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES =
  "0x3d79EdAaBC0EaB6F08ED885C05Fc0B014290D95A";
const CELO_TICK_LENS_ADDRESSES = "0x5f115D9113F88e0a0Db1b5033D90D4a9690AcD3D";

// BNB v3 addresses
const BNB_V3_CORE_FACTORY_ADDRESSES =
  "0xdB1d10011AD0Ff90774D0C6Bb92e5C5c8b4461F7";
const BNB_V3_MIGRATOR_ADDRESSES = "0x32681814957e0C13117ddc0c2aba232b5c9e760f";
const BNB_MULTICALL_ADDRESS = "0x963Df249eD09c358A4819E39d9Cd5736c3087184";
const BNB_QUOTER_ADDRESSES = "0x78D78E420Da98ad378D7799bE8f4AF69033EB077";
const BNB_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES =
  "0x7b8A01B39D58278b5DE7e48c8449c9f4F5170613";
const BNB_TICK_LENS_ADDRESSES = "0xD9270014D396281579760619CCf4c3af0501A47C";

// optimism goerli addresses
const OPTIMISM_GOERLI_V3_CORE_FACTORY_ADDRESSES =
  "0xB656dA17129e7EB733A557f4EBc57B76CFbB5d10";
const OPTIMISM_GOERLI_V3_MIGRATOR_ADDRESSES =
  "0xf6c55fBe84B1C8c3283533c53F51bC32F5C7Aba8";
const OPTIMISM_GOERLI_MULTICALL_ADDRESS =
  "0x07F2D8a2a02251B62af965f22fC4744A5f96BCCd";
const OPTIMISM_GOERLI_QUOTER_ADDRESSES =
  "0x9569CbA925c8ca2248772A9A4976A516743A246F";
const OPTIMISM_GOERLI_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES =
  "0x39Ca85Af2F383190cBf7d7c41ED9202D27426EF6";
const OPTIMISM_GOERLI_TICK_LENS_ADDRESSES =
  "0xe6140Bd164b63E8BfCfc40D5dF952f83e171758e";

// arbitrum goerli v3 addresses
const ARBITRUM_GOERLI_V3_CORE_FACTORY_ADDRESSES =
  "0x4893376342d5D7b3e31d4184c08b265e5aB2A3f6";
const ARBITRUM_GOERLI_V3_MIGRATOR_ADDRESSES =
  "0xA815919D2584Ac3F76ea9CB62E6Fd40a43BCe0C3";
const ARBITRUM_GOERLI_MULTICALL_ADDRESS =
  "0x8260CB40247290317a4c062F3542622367F206Ee";
const ARBITRUM_GOERLI_QUOTER_ADDRESSES =
  "0x1dd92b83591781D0C6d98d07391eea4b9a6008FA";
const ARBITRUM_GOERLI_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES =
  "0x622e4726a167799826d1E1D150b076A7725f5D81";
const ARBITRUM_GOERLI_TICK_LENS_ADDRESSES =
  "0xb52429333da969a0C79a60930a4Bf0020E5D1DE8";

/* V3 Contract Addresses */
export const V3_CORE_FACTORY_ADDRESSES: AddressMap = {
  ...constructSameAddressMap(V3_FACTORY_ADDRESS, [
    SupportedChainId.OPTIMISM,
    SupportedChainId.ARBITRUM_ONE,
    SupportedChainId.POLYGON_MUMBAI,
    SupportedChainId.POLYGON,
  ]),
  [SupportedChainId.CELO]: CELO_V3_CORE_FACTORY_ADDRESSES,
  [SupportedChainId.CELO_ALFAJORES]: CELO_V3_CORE_FACTORY_ADDRESSES,
  [SupportedChainId.BNB]: BNB_V3_CORE_FACTORY_ADDRESSES,
  [SupportedChainId.OPTIMISM_GOERLI]: OPTIMISM_GOERLI_V3_CORE_FACTORY_ADDRESSES,
  [SupportedChainId.ARBITRUM_GOERLI]: ARBITRUM_GOERLI_V3_CORE_FACTORY_ADDRESSES,

  [SupportedChainId.HARMONY]: "0xe62B3e1D1F2D58Dd9F95f3CdF847acfc8177e5ea",
};

export const V3_MIGRATOR_ADDRESSES: AddressMap = {
  ...constructSameAddressMap("0xA5644E29708357803b5A882D272c41cC0dF92B34", [
    SupportedChainId.ARBITRUM_ONE,
    SupportedChainId.POLYGON_MUMBAI,
    SupportedChainId.POLYGON,
  ]),
  [SupportedChainId.CELO]: CELO_V3_MIGRATOR_ADDRESSES,
  [SupportedChainId.CELO_ALFAJORES]: CELO_V3_MIGRATOR_ADDRESSES,
  [SupportedChainId.BNB]: BNB_V3_MIGRATOR_ADDRESSES,
  [SupportedChainId.OPTIMISM_GOERLI]: OPTIMISM_GOERLI_V3_MIGRATOR_ADDRESSES,
  [SupportedChainId.ARBITRUM_GOERLI]: ARBITRUM_GOERLI_V3_MIGRATOR_ADDRESSES,

  [SupportedChainId.HARMONY]: "0x77cF3641E086BccB759ceA8713fBF3b00ba3EB9C",
};

export const MULTICALL_ADDRESS: AddressMap = {
  ...constructSameAddressMap("0x1F98415757620B543A52E61c46B32eB19261F984", [
    SupportedChainId.OPTIMISM,
    SupportedChainId.POLYGON_MUMBAI,
    SupportedChainId.POLYGON,
  ]),
  [SupportedChainId.ARBITRUM_ONE]: "0xadF885960B47eA2CD9B55E6DAc6B42b7Cb2806dB",
  [SupportedChainId.CELO]: CELO_MULTICALL_ADDRESS,
  [SupportedChainId.CELO_ALFAJORES]: CELO_MULTICALL_ADDRESS,
  [SupportedChainId.BNB]: BNB_MULTICALL_ADDRESS,
  [SupportedChainId.OPTIMISM_GOERLI]: OPTIMISM_GOERLI_MULTICALL_ADDRESS,
  [SupportedChainId.ARBITRUM_GOERLI]: ARBITRUM_GOERLI_MULTICALL_ADDRESS,

  [SupportedChainId.HARMONY]: "0xaC08159ebee622c66461B96024CAdA21891eE82E",
};

/**
 * The oldest V0 governance address
 */
export const GOVERNANCE_ALPHA_V0_ADDRESSES: AddressMap =
  constructSameAddressMap("0x5e4be8Bc9637f0EAA1A755019e06A68ce081D58F");
/**
 * The older V1 governance address
 */
export const GOVERNANCE_ALPHA_V1_ADDRESSES: AddressMap = {
  [SupportedChainId.MAINNET]: "0xC4e172459f1E7939D522503B81AFAaC1014CE6F6",
};
/**
 * The latest governor bravo that is currently admin of timelock
 */
export const GOVERNANCE_BRAVO_ADDRESSES: AddressMap = {
  [SupportedChainId.MAINNET]: "0x408ED6354d4973f66138C91495F2f2FCbd8724C3",
};

export const TIMELOCK_ADDRESS: AddressMap = constructSameAddressMap(
  "0x1a9C8182C09F50C8318d769245beA52c32BE35BC"
);

export const MERKLE_DISTRIBUTOR_ADDRESS: AddressMap = {
  [SupportedChainId.MAINNET]: "0x090D4613473dEE047c3f2706764f49E0821D256e",
};

export const ARGENT_WALLET_DETECTOR_ADDRESS: AddressMap = {
  [SupportedChainId.MAINNET]: "0xeca4B0bDBf7c55E9b7925919d03CbF8Dc82537E8",
};

export const QUOTER_ADDRESSES: AddressMap = {
  ...constructSameAddressMap("0xb27308f9F90D607463bb33eA1BeBb41C27CE5AB6", [
    SupportedChainId.OPTIMISM,
    SupportedChainId.ARBITRUM_ONE,
    SupportedChainId.POLYGON_MUMBAI,
    SupportedChainId.POLYGON,
  ]),
  [SupportedChainId.CELO]: CELO_QUOTER_ADDRESSES,
  [SupportedChainId.CELO_ALFAJORES]: CELO_QUOTER_ADDRESSES,
  [SupportedChainId.BNB]: BNB_QUOTER_ADDRESSES,
  [SupportedChainId.OPTIMISM_GOERLI]: OPTIMISM_GOERLI_QUOTER_ADDRESSES,
  [SupportedChainId.ARBITRUM_GOERLI]: ARBITRUM_GOERLI_QUOTER_ADDRESSES,

  [SupportedChainId.HARMONY]: "0xD2B74B1e71De89B36f3262A104F5D08e849415F4",
};

export const NONFUNGIBLE_POSITION_MANAGER_ADDRESSES: AddressMap = {
  ...constructSameAddressMap("0xC36442b4a4522E871399CD717aBDD847Ab11FE88", [
    SupportedChainId.OPTIMISM,
    SupportedChainId.ARBITRUM_ONE,
    SupportedChainId.POLYGON_MUMBAI,
    SupportedChainId.POLYGON,
  ]),
  [SupportedChainId.CELO]: CELO_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
  [SupportedChainId.CELO_ALFAJORES]:
    CELO_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
  [SupportedChainId.BNB]: BNB_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
  [SupportedChainId.OPTIMISM_GOERLI]:
    OPTIMISM_GOERLI_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,
  [SupportedChainId.ARBITRUM_GOERLI]:
    ARBITRUM_GOERLI_NONFUNGIBLE_POSITION_MANAGER_ADDRESSES,

  [SupportedChainId.HARMONY]: "0x5b2449B5B25d8430f60C60BaEaa9655A5a06a19e",
};

export const ENS_REGISTRAR_ADDRESSES: AddressMap = {
  [SupportedChainId.MAINNET]: "0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e",
  [SupportedChainId.GOERLI]: "0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e",
};

export const SOCKS_CONTROLLER_ADDRESSES: AddressMap = {
  [SupportedChainId.MAINNET]: "0x65770b5283117639760beA3F867b69b3697a91dd",
};

export const TICK_LENS_ADDRESSES: AddressMap = {
  [SupportedChainId.ARBITRUM_ONE]: "0xbfd8137f7d1516D3ea5cA83523914859ec47F573",
  [SupportedChainId.ARBITRUM_GOERLI]: ARBITRUM_GOERLI_TICK_LENS_ADDRESSES,
  [SupportedChainId.CELO]: CELO_TICK_LENS_ADDRESSES,
  [SupportedChainId.CELO_ALFAJORES]: CELO_TICK_LENS_ADDRESSES,
  [SupportedChainId.BNB]: BNB_TICK_LENS_ADDRESSES,
  [SupportedChainId.OPTIMISM_GOERLI]: OPTIMISM_GOERLI_TICK_LENS_ADDRESSES,

  [SupportedChainId.HARMONY]: "0x451E25aFdE8ff2A902905847Fdc681616b008d03",
};
